import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Shield, MapPin, Star, Sparkles, Car, Coffee, Castle, MountainSnow, RollerCoaster, Utensils, ShoppingBag, Award, BadgeCheck, ArrowLeft, Heart, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MeetMiniStripes = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16" data-testid="meet-mini-stripes-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Meet Mini Stripes UK
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From our Midlands workshop — the historic home of Mini heritage
            <br />
            we design and create products made for Mini lovers
          </p>
        </div>

        {/* The ZenGraphicsUK Story Section */}
        <div className="mb-12">
          <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-3xl p-8 md:p-10 shadow-sm" data-testid="story-section">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                The Ministripes UK Story
              </h2>
              <div className="w-24 h-1 bg-black mx-auto rounded-full"></div>
            </div>
            
            <div className="space-y-6 text-gray-700 leading-relaxed" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
              <p className="text-lg">
                From a young lad growing up in a military family, moving from school to school, The founder of Ministripes UK never really had the chance to settle into education. School wasn't where he thrived — <span className="font-semibold text-gray-900">creativity was</span>. At just 14, he left school to work alongside his dad, who had turned sign‑making from a hobby into a small mobile sign shop run out of a van. Together, with no formal training, they learned the trade through self‑tuition, trial and error, and guidance from other companies willing to share their knowledge.
              </p>
              
              <p className="text-lg">
                It didn't take long for The founder of Ministripes UK to realise that this industry was where he belonged. The creative spark, the hands‑on work, the satisfaction of seeing something physical come to life — it all clicked. After years of learning the craft, he stepped out on his own. With fresh energy and ambition, he built his own venture, pushing deeper into fabrication, large‑format graphics, and fleet work. Over the next two decades, The founder of Ministripes UK became well‑known across the Midlands, trusted by major clients and recognised for <span className="font-semibold text-gray-900">quality and reliability</span>.
              </p>
              
              <p className="text-lg">
                But success came with pressure. Endless deadlines, long hours on artwork that could be scrapped in a moment, the cost and stress of large illuminated signage, and the constant worry of third‑party contractors — it all took its toll. Something had to change.
              </p>
              
              <div className="bg-white/60 rounded-2xl p-6 my-8 border-l-4 border-black">
                <p className="text-lg italic">
                  Then came COVID. Instead of stepping back, The founder of Ministripes UK and the team took a bold risk: shifting 90% of the business online. Car graphics had always been their strongest area — the place where creativity, precision, and passion came together naturally. And it paid off. The online business exploded, and <span className="font-semibold text-gray-900">Ministripes UK quickly became known for exceptional quality, service, and attention to detail</span>.
                </p>
              </div>
              
              <p className="text-lg">
                Today, the mission is clearer than ever. The team has found a new passion and a new community — <span className="font-semibold text-gray-900">the Mini world</span>. And this time, it's personal. This is the final venture, the one that gets everything they've got. The goal is simple: to bring Mini lovers something they've never seen before, with craftsmanship, creativity, and heart at the centre of it all.
              </p>
              
              <div className="text-center pt-6">
                <div className="inline-flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <Rocket className="h-6 w-6" />
                  <span>This is just the beginning of the next chapter.</span>
                </div>
                <p className="text-lg text-gray-600 mt-2">And the journey ahead looks exciting.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Fitting Service Section */}
        <div className="mb-8">
          <div className="bg-gray-100 rounded-3xl p-8" data-testid="fitting-service-section">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <BadgeCheck className="h-10 w-10 text-gray-800" />
                <h2 
                  className="text-lg font-semibold text-gray-800"
                  style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                >
                  Professional Car Graphics Supply & Fitting
                </h2>
              </div>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <MapPin className="h-5 w-5" />
                <span className="text-lg">Tamworth, Midlands</span>
              </div>
            </div>

            <p 
              className="text-center text-muted-foreground text-lg mb-8 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              We supply and fit high-quality car graphics, stripes, and decals, offering a full professional fitting service right here in Tamworth.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {/* 5-Star Facility */}
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                  <Star className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="font-bold text-sm mb-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  5-Star Facility
                </h3>
                <p className="text-sm text-muted-foreground">
                  Air-conditioned work bay, clean & modern
                </p>
              </div>

              {/* Expert Fitting */}
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                  <Car className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="font-bold text-sm mb-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  Expert Fitting
                </h3>
                <p className="text-sm text-muted-foreground">
                  Professional installation from start to finish
                </p>
              </div>

              {/* Convenient Location */}
              <div className="flex flex-col items-center text-center p-4">
                <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                  <Coffee className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="font-bold text-sm mb-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  Convenient Location
                </h3>
                <p className="text-sm text-muted-foreground">
                  Minutes from Tamworth's shops, cafés & attractions
                </p>
              </div>
            </div>

            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Castle className="h-7 w-7 text-muted-foreground" />
                <MountainSnow className="h-7 w-7 text-muted-foreground" />
                <RollerCoaster className="h-7 w-7 text-muted-foreground" />
                <Utensils className="h-7 w-7 text-muted-foreground" />
                <ShoppingBag className="h-7 w-7 text-muted-foreground" />
              </div>
              <p 
                className="text-muted-foreground text-lg italic"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Plenty to see and do while your graphics are expertly fitted!
              </p>
            </div>

            <div className="text-center">
              <Link to="/contact">
                <Button 
                  className="rounded-full px-8 py-3 text-base font-medium"
                  data-testid="book-appointment-btn"
                >
                  Book an Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Sytner Approved Supplier Section */}
        <div className="mb-8">
          <div className="bg-gray-100 rounded-3xl p-8" data-testid="sytner-section">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BadgeCheck className="h-10 w-10 text-gray-800" />
              <p 
                className="text-center text-gray-800 text-lg font-semibold"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Fitters for Mini Dealerships
              </p>
            </div>
            
            <div className="space-y-4 max-w-3xl mx-auto">
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                We are a trusted supplier of graphics for Sytner Mini dealerships, delivering consistent quality, reliability, and a fully professional service. Our support includes both replacement graphics and fully custom-designed, in-house manufactured solutions tailored to dealership requirements.
              </p>
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                With a focus on precision, durability, and brand presentation, we ensure every graphic is produced and fitted to the highest standard—reinforcing the reputation and customer experience expected across the Sytner network.
              </p>
            </div>

            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">OEM Quality</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">Trusted Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">Premium Finish</span>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Vehicle Wrapping Service Section */}
        <div className="mb-8">
          <div className="bg-gray-100 rounded-3xl p-8" data-testid="wrapping-service-section">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="h-10 w-10 text-gray-800" />
              <h2 
                className="text-center text-gray-800 text-lg font-semibold"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Premium Vehicle Wrapping Service
              </h2>
            </div>
            
            <div className="space-y-4 max-w-3xl mx-auto mb-6">
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                We provide a premium vehicle wrapping service tailored for customers who expect exceptional quality and craftsmanship. Our wraps are supplied and fitted in a fully air‑conditioned, meticulously maintained work bay designed to deliver flawless, high‑end results.
              </p>
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                As experienced specialists and certified trainers in Hexis wrap films, we bring industry‑leading expertise to every project. Our range includes an extensive selection of luxury colours and finishes from all top wrap brands, with a curated collection available to view here:{" "}
                <Link to="/products/wrap-vinyl" className="text-black underline hover:no-underline font-medium">
                  View Our Wrap Vinyl Collection
                </Link>.
              </p>
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Whether you're seeking a sophisticated colour change or a bespoke finish, we offer a seamless, professional experience from consultation to completion. Appointments and quotations are available upon request.
              </p>
            </div>

            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">Hexis Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">Premium Finish</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground text-sm">Expert Fitting</span>
              </div>
            </div>

            <div className="text-center">
              <Link to="/contact">
                <Button 
                  className="rounded-full px-8 py-3 text-base font-medium"
                  data-testid="contact-quote-btn"
                >
                  Contact Us for a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetMiniStripes;
